/**
 * CRM Feature Flags
 * Centralized feature flag management for CRM modules.
 * All CRM feature flags should be read through this module.
 */

/**
 * Get a feature flag value from environment variables.
 * 
 * @param {string} flagName - Name of the feature flag (e.g., 'REACT_APP_CRM_ENABLED')
 * @param {boolean} defaultValue - Default value if flag is not set (default: false)
 * @returns {boolean} Feature flag value (true/false)
 */
export const getFeatureFlag = (flagName, defaultValue = false) => {
  const value = process.env[flagName];
  if (value === undefined || value === null) {
    return defaultValue;
  }
  // Handle various truthy/falsy string representations
  const normalized = String(value).toLowerCase().trim();
  return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on' || normalized === 'enabled';
};

// CRM Feature Flags
// Note: React environment variables must be prefixed with REACT_APP_
export const CRM_ENABLED = getFeatureFlag('REACT_APP_CRM_ENABLED', false);
export const CRM_NOTIFICATIONS_ENABLED = getFeatureFlag('REACT_APP_CRM_NOTIFICATIONS_ENABLED', false);
export const CRM_BENEFITS_ENABLED = getFeatureFlag('REACT_APP_CRM_BENEFITS_ENABLED', true);
export const CRM_FORMS_DYNAMIC = getFeatureFlag('REACT_APP_CRM_FORMS_DYNAMIC', true);

/**
 * Check if CRM is enabled
 * @returns {boolean}
 */
export const isCrmEnabled = () => CRM_ENABLED;

/**
 * Check if CRM notifications are enabled
 * @returns {boolean}
 */
export const isCrmNotificationsEnabled = () => CRM_ENABLED && CRM_NOTIFICATIONS_ENABLED;

/**
 * Check if CRM benefits tracking is enabled
 * @returns {boolean}
 */
export const isCrmBenefitsEnabled = () => CRM_ENABLED && CRM_BENEFITS_ENABLED;

/**
 * Check if CRM forms are dynamic
 * @returns {boolean}
 */
export const isCrmFormsDynamic = () => CRM_ENABLED && CRM_FORMS_DYNAMIC;
